import {
  __export
} from "./chunk-IEP6GGEX.mjs";

// src/OpfsPool.ts
var OpfsPool_exports = {};
__export(OpfsPool_exports, {
  DEFAULT_DB_FILENAME: () => DEFAULT_DB_FILENAME,
  isValidSqliteDatabase: () => isValidSqliteDatabase,
  listFiles: () => listFiles,
  readDatabase: () => readDatabase,
  wipe: () => wipe,
  writeDatabase: () => writeDatabase
});
var DEFAULT_DB_FILENAME = "DXOS";
var OPFS_VFS_DIRECTORY = "opfs";
var HEADER_MAX_PATH_SIZE = 512;
var HEADER_FLAGS_SIZE = 4;
var HEADER_DIGEST_SIZE = 8;
var HEADER_CORPUS_SIZE = HEADER_MAX_PATH_SIZE + HEADER_FLAGS_SIZE;
var HEADER_OFFSET_FLAGS = HEADER_MAX_PATH_SIZE;
var HEADER_OFFSET_DIGEST = HEADER_CORPUS_SIZE;
var HEADER_OFFSET_DATA = 4096;
var SQLITE_OPEN_READWRITE = 2;
var SQLITE_OPEN_CREATE = 4;
var SQLITE_OPEN_MAIN_DB = 256;
var MAIN_DB_FLAGS = SQLITE_OPEN_READWRITE | SQLITE_OPEN_CREATE | SQLITE_OPEN_MAIN_DB;
var DEFAULT_POOL_CAPACITY = 6;
var SQLITE_MAGIC = new TextEncoder().encode("SQLite format 3\0");
var computeDigest = (corpus) => {
  if (!corpus[0]) {
    return new Uint32Array([
      4274806656,
      2899230775
    ]);
  }
  let h1 = 3735928559;
  let h2 = 1103547991;
  for (const value of corpus) {
    h1 = Math.imul(h1 ^ value, 2654435761);
    h2 = Math.imul(h2 ^ value, 1597334677);
  }
  h1 = Math.imul(h1 ^ h1 >>> 16, 2246822507) ^ Math.imul(h2 ^ h2 >>> 13, 3266489909);
  h2 = Math.imul(h2 ^ h2 >>> 16, 2246822507) ^ Math.imul(h1 ^ h1 >>> 13, 3266489909);
  return new Uint32Array([
    h1 >>> 0,
    h2 >>> 0
  ]);
};
var readAssociatedPath = async (fileHandle) => {
  const file = await fileHandle.getFile();
  if (file.size < HEADER_OFFSET_DIGEST + HEADER_DIGEST_SIZE) {
    return "";
  }
  const corpus = new Uint8Array(await file.slice(0, HEADER_CORPUS_SIZE).arrayBuffer());
  const digestBytes = new Uint8Array(await file.slice(HEADER_OFFSET_DIGEST, HEADER_OFFSET_DIGEST + HEADER_DIGEST_SIZE).arrayBuffer());
  const fileDigest = new Uint32Array(digestBytes.buffer, digestBytes.byteOffset, HEADER_DIGEST_SIZE / 4);
  const computedDigest = computeDigest(corpus);
  if (!fileDigest.every((value, index) => value === computedDigest[index])) {
    return "";
  }
  const pathEnd = corpus.indexOf(0);
  return pathEnd <= 0 ? "" : new TextDecoder().decode(corpus.subarray(0, pathEnd));
};
var buildHeader = (path, flags) => {
  const header = new Uint8Array(HEADER_OFFSET_DATA);
  const corpus = header.subarray(0, HEADER_CORPUS_SIZE);
  const encoded = new TextEncoder().encodeInto(path, corpus);
  if (encoded.written !== void 0 && encoded.written >= HEADER_MAX_PATH_SIZE) {
    throw new Error("OPFS associated path too long");
  }
  const dataView = new DataView(corpus.buffer, corpus.byteOffset, corpus.byteLength);
  dataView.setUint32(HEADER_OFFSET_FLAGS, flags);
  const digest = computeDigest(corpus);
  header.set(new Uint8Array(digest.buffer), HEADER_OFFSET_DIGEST);
  return header;
};
var copyToArrayBuffer = (bytes) => {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy;
};
var writeAssociatedPath = async (fileHandle, path, flags) => {
  const header = buildHeader(path, flags);
  const writable = await fileHandle.createWritable({
    keepExistingData: false
  });
  try {
    await writable.write(copyToArrayBuffer(header));
  } finally {
    await writable.close();
  }
};
var getOpfsDirectory = async () => {
  let handle = await navigator.storage.getDirectory();
  for (const segment of OPFS_VFS_DIRECTORY.split("/")) {
    if (segment) {
      handle = await handle.getDirectoryHandle(segment, {
        create: true
      });
    }
  }
  return handle;
};
var openPoolFiles = async (directory) => {
  const files = [];
  for await (const [name, handle] of directory.entries()) {
    if (handle.kind !== "file") {
      continue;
    }
    const fileHandle = handle;
    files.push({
      name,
      handle: fileHandle,
      associatedPath: await readAssociatedPath(fileHandle)
    });
  }
  return files;
};
var isValidSqliteDatabase = (bytes) => {
  if (bytes.byteLength < SQLITE_MAGIC.byteLength) {
    return false;
  }
  for (let index = 0; index < SQLITE_MAGIC.byteLength; index++) {
    if (bytes[index] !== SQLITE_MAGIC[index]) {
      return false;
    }
  }
  return true;
};
var listFiles = async () => {
  const directory = await getOpfsDirectory();
  const files = await openPoolFiles(directory);
  const summaries = [];
  for (const file of files) {
    const blob = await file.handle.getFile();
    summaries.push({
      name: file.name,
      associatedPath: file.associatedPath,
      totalBytes: blob.size,
      payloadBytes: Math.max(0, blob.size - HEADER_OFFSET_DATA)
    });
  }
  return summaries.sort((left, right) => right.payloadBytes - left.payloadBytes);
};
var associatedPathForDb = (dbFilename) => `/${dbFilename}`;
var generatePoolFileName = () => crypto.randomUUID().replaceAll("-", "").slice(0, 11);
var readSqliteMagic = async (fileHandle) => {
  const file = await fileHandle.getFile();
  const payloadBytes = Math.max(0, file.size - HEADER_OFFSET_DATA);
  if (payloadBytes <= 0) {
    return {
      payloadBytes: 0,
      magic: new Uint8Array(0)
    };
  }
  const magic = new Uint8Array(await file.slice(HEADER_OFFSET_DATA, HEADER_OFFSET_DATA + 16).arrayBuffer());
  return {
    payloadBytes,
    magic
  };
};
var readDatabase = async (dbFilename = DEFAULT_DB_FILENAME) => {
  const associatedPath = associatedPathForDb(dbFilename);
  const directory = await getOpfsDirectory();
  const files = await openPoolFiles(directory);
  let best;
  let bestPayloadBytes = 0;
  for (const file2 of files) {
    const { payloadBytes, magic } = await readSqliteMagic(file2.handle);
    if (payloadBytes <= bestPayloadBytes) {
      continue;
    }
    if (magic.byteLength < 16 || new TextDecoder().decode(magic.subarray(0, 15)) !== "SQLite format 3") {
      continue;
    }
    best = file2;
    bestPayloadBytes = payloadBytes;
  }
  const match = files.find((file2) => file2.associatedPath === associatedPath && file2 === best) ?? files.find((file2) => file2.associatedPath === associatedPath) ?? best;
  if (!match) {
    throw new Error(`OPFS database not found at ${associatedPath}`);
  }
  const file = await match.handle.getFile();
  const payloadSize = file.size - HEADER_OFFSET_DATA;
  if (payloadSize <= 0) {
    return new Uint8Array(0);
  }
  return new Uint8Array(await file.slice(HEADER_OFFSET_DATA).arrayBuffer());
};
var writeDatabase = async (database, dbFilename = DEFAULT_DB_FILENAME) => {
  const associatedPath = associatedPathForDb(dbFilename);
  const directory = await getOpfsDirectory();
  const files = await openPoolFiles(directory);
  let target = files.find((file) => file.associatedPath === associatedPath);
  if (!target) {
    target = files.find((file) => !file.associatedPath);
  }
  let createdTarget = false;
  if (!target) {
    const fileHandle = await directory.getFileHandle(generatePoolFileName(), {
      create: true
    });
    target = {
      name: fileHandle.name,
      handle: fileHandle,
      associatedPath: ""
    };
    createdTarget = true;
  }
  const header = buildHeader(associatedPath, MAIN_DB_FLAGS);
  const writable = await target.handle.createWritable({
    keepExistingData: false
  });
  try {
    await writable.write(copyToArrayBuffer(header));
    await writable.write(copyToArrayBuffer(database));
  } finally {
    await writable.close();
  }
  const staleSidecars = /* @__PURE__ */ new Set([
    `${associatedPath}-journal`,
    `${associatedPath}-wal`,
    `${associatedPath}-shm`
  ]);
  for (const file of files) {
    if (staleSidecars.has(file.associatedPath)) {
      await writeAssociatedPath(file.handle, "", 0);
    }
  }
  const totalFiles = files.length + (createdTarget ? 1 : 0);
  for (let index = totalFiles; index < DEFAULT_POOL_CAPACITY; index++) {
    const spareHandle = await directory.getFileHandle(generatePoolFileName(), {
      create: true
    });
    await writeAssociatedPath(spareHandle, "", 0);
  }
};
var wipe = async () => {
  const root = await navigator.storage.getDirectory();
  try {
    await root.removeEntry(OPFS_VFS_DIRECTORY, {
      recursive: true
    });
  } catch (error) {
    if (!(error instanceof DOMException) || error.name !== "NotFoundError") {
      throw error;
    }
  }
};

export {
  DEFAULT_DB_FILENAME,
  isValidSqliteDatabase,
  listFiles,
  readDatabase,
  writeDatabase,
  wipe,
  OpfsPool_exports
};
//# sourceMappingURL=chunk-HK3AUJTP.mjs.map
